import React, { useState } from 'react';
import PropTypes from 'prop-types';
import CanPerform from 'components/shared/CanPerform';
import { CHECKOUT } from 'constants/afterSalesShortcuts';
import { FormattedMessage } from 'react-intl';
import { bookingShape } from 'constants/propTypes';

import CheckoutTicketsModal from 'components/shared/CheckoutTicketsModal';

import { useManagedMutation } from 'utils/managedHooks';

import { Spinner } from 'admin-components/components';
import { Row, Column } from 'components/shared/Layout';
import { CHECKOUT_TICKETS, ADD_ADDITIONAL_DETAILS } from '../../../../../store/mutations';
import { CtaButton } from '../AfterSalesCtas.styles';

import {
  runAfterSaleValidator,
  selectedProductsCheckedIn,
  getTicketNumberForCheckedInTickets,
  bookingIsConfirmed,
  allProductsHasNotBeenPrrRefunded,
  selectedItemsArePartOfASummatedFare,
  areSomeExternalCarrierProductsSelected
} from '../AfterSalesCtas.utils';

const CheckoutCta = ({ booking, selectedItemRefs, disabled, isOutboundSummatedFare = false, isInboundSummatedFare = false }) => {
  const [enterReasonVisible, setEnterReasonVisible] = useState(false);

  const [checkoutTickets, { loading }] = useManagedMutation(CHECKOUT_TICKETS, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading }] = useManagedMutation(ADD_ADDITIONAL_DETAILS);

  const areItemsInASummatedFare = selectedItemsArePartOfASummatedFare(
    booking,
    selectedItemRefs,
    isOutboundSummatedFare,
    isInboundSummatedFare
  );

  const hasExternalCarriersSelected = areSomeExternalCarrierProductsSelected({
    selectedItemRefs,
    journeys: [...(booking.outbound || []), ...(booking.inbound || [])]
  });
  const isDisabled =
    disabled ||
    (areItemsInASummatedFare && hasExternalCarriersSelected) ||
    !runAfterSaleValidator(
      booking,
      selectedItemRefs
    )([bookingIsConfirmed, selectedProductsCheckedIn('every'), allProductsHasNotBeenPrrRefunded]);

  const checkedInTickets = getTicketNumberForCheckedInTickets({
    booking,
    selectedItemRefs
  });

  if (loading || addAdditionalDetailsLoading) {
    return (
      <Row span={16}>
        <Column justifyContent="center" alignItems="center">
          <Spinner type={'circular'} />
        </Column>
      </Row>
    );
  }

  const additionalDetailsPerTickets = ({ reason }) =>
    checkedInTickets.map((item) => {
      const product = [...booking.outbound, ...booking.inbound].find(({ products }) =>
        products.some(({ ticketNumber }) => ticketNumber === item)
      );
      const findRef = booking.allBookingItems.find((product) => product.ticketNumber === item);

      return {
        itemRefId: Number(findRef.itemRef),
        key: 'CheckOut_Reason',
        value: `${product?.serviceType?.code || 'ES'} ticket: ${item}, itemRef: ${findRef.itemRef}. Reason: ${reason}`
      };
    });

  const handleSubmit = async ({ reason }) => {
    // eslint-disable-next-line array-callback-return
    await addAdditionalDetails({
      variables: {
        bookingReference: booking.reference,
        additionalDetails: additionalDetailsPerTickets({ reason })
      }
    });

    setEnterReasonVisible(!enterReasonVisible);

    await checkoutTickets({
      variables: {
        ticketNumbers: checkedInTickets,
        bookingReference: booking.reference
      }
    });
  };

  return (
    <>
      {enterReasonVisible && (
        <CheckoutTicketsModal
          isOpen={enterReasonVisible}
          isLoading={loading}
          onToggle={() => setEnterReasonVisible(!enterReasonVisible)}
          onSubmit={handleSubmit}
          checkedInTickets={checkedInTickets}
        />
      )}
      <CanPerform afterSalesShortcut={CHECKOUT}>
        {(isEligible) =>
          isEligible && (
            <CtaButton
              onClick={() => setEnterReasonVisible(true)}
              data-cy="checkout-cta"
              label={<FormattedMessage id="afterSales.checkout" />}
              disabled={isDisabled}
            />
          )
        }
      </CanPerform>
    </>
  );
};

CheckoutCta.propTypes = {
  booking: bookingShape,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  isOutboundSummatedFare: PropTypes.bool,
  isInboundSummatedFare: PropTypes.bool
};

export default CheckoutCta;
